<template>
  <div :class="rootKls">
    <div
      role="tab"
      :aria-expanded="isActive"
      :aria-controls="scopedContentId"
      :aria-describedby="scopedContentId"
    >
      <div
        :id="scopedHeadId"
        :class="headKls"
        role="button"
        :tabindex="disabled ? -1 : 0"
        data-component-name="Collapse"
        :data-event-name="isActive ? 'collapse' : 'expand'"
        :data-event-content="isActive ? '折叠' : '展开'"
        @click="handleHeaderClick"
        @keypress.space.enter.stop.prevent="handleEnterClick"
        @focus="handleFocus"
        @blur="focusing = false"
      >
        <jd-icon v-if="expandIconPosition === 'start'" :class="arrowKls">
          <arrow-right />
        </jd-icon>
        <slot name="title">{{ title }}</slot>
        <!-- 右侧展开/折叠图标前的自定义额外图标，仅在图标位于右侧时展示 -->
        <span
          v-if="$slots['extra-icon'] || extraIcon"
          :class="extraIconKls"
          @click.stop="handleExtraIconClick"
        >
          <slot name="extra-icon">
            <jd-icon v-if="extraIcon">
              <component :is="extraIcon" />
            </jd-icon>
          </slot>
        </span>
        <jd-icon v-if="expandIconPosition !== 'start'" :class="arrowKls">
          <arrow-right />
        </jd-icon>
      </div>
    </div>
    <jd-collapse-transition>
      <div
        v-show="isActive"
        :id="scopedContentId"
        :class="itemWrapperKls"
        role="tabpanel"
        :aria-hidden="!isActive"
        :aria-labelledby="scopedHeadId"
      >
        <div :class="itemContentKls">
          <slot />
        </div>
      </div>
    </jd-collapse-transition>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import JdCollapseTransition from '@jdesign-vue/components/collapse-transition'
import JdIcon from '@jdesign-vue/components/icon'
import { useNamespace } from '@jdesign-vue/hooks'
import { AngleRight as ArrowRight } from '@jd/jdesign-icons-vue'
import { collapseItemProps } from './collapse-item'
import { useCollapseItem, useCollapseItemDOM } from './use-collapse-item'

defineOptions({
  name: 'JdCollapseItem',
})

const props = defineProps(collapseItemProps)
const emit = defineEmits<{
  (e: 'extra-icon-click', evt: MouseEvent): void
}>()

const {
  focusing,
  id,
  isActive,
  expandIconPosition,
  handleFocus,
  handleHeaderClick,
  handleEnterClick,
} = useCollapseItem(props)

const {
  arrowKls,
  headKls,
  rootKls,
  itemWrapperKls,
  itemContentKls,
  scopedContentId,
  scopedHeadId,
} = useCollapseItemDOM(props, { focusing, isActive, id, expandIconPosition })

const ns = useNamespace('collapse')
const extraIconKls = computed(() => ns.be('item', 'extra-icon'))

const handleExtraIconClick = (evt: MouseEvent) => {
  emit('extra-icon-click', evt)
}

defineExpose({
  /** @description current collapse-item whether active */
  isActive,
})
</script>
